import { Fragment, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { get, type AdminStream, type TranscodeJobAdmin, type TranscodeNodeJob, type TranscoderInfo } from '../../api'
import { Icon } from '../../ui'
import { canBlend, freeWithoutMark } from '../../live-ui/watermark'
import { useMedia } from '../../live-ui/studio/useLiveScreen'
import { GearIcon } from './StatusCell'
import type { Spec } from './states'
import { Empty, ErrorBox, Fact, NetPanel, SkeletonRows, StateBadge } from './ui'

/*
 * Network › Transcoders (TRANS-1, docs/architecture/transcoder.md §9.1): the
 * transcoder nodes (role transcoder, our GPU engine) and the live jobs, in the
 * layout of Network › Captions. Per node: engine and video card, jobs of its
 * capacity, saturated (a job slower than real time: no new jobs). Per job:
 * what it makes, speed, latency, restarts, where it runs. GPU load and memory
 * are not in the API yet (the engine reports them to the node only) — the
 * table shows what the center knows. Scheduled machines of kind transcoder
 * live in their own panel (T6). English only, like the rest of the console.
 * WM3 (WMU-07): the tag BLEND on an engine that burns in the free plan's Strevix mark, WM on its
 * jobs, and how many free broadcasts go out without the mark now (in the capacity line, warn).
 */

export const TRANSCODERS_KEY = ['admin-transcoders']
export const TRANSCODE_JOBS_KEY = ['admin-transcode-jobs']

type NodeState = 'online' | 'saturated' | 'engine_down' | 'offline'
const NODE_SPEC: Record<NodeState, Spec> = {
  online: { glyph: '●', word: 'Online', color: 'live', border: 'solid', hint: 'Connected, the engine is up' },
  saturated: { glyph: '▲', word: 'Saturated', color: 'warn', border: 'dashed', hint: 'A job runs slower than real time: no new jobs here' },
  engine_down: { glyph: '◔', word: 'Engine down', color: 'warn', border: 'dashed', hint: 'Connected, but its GPU engine is not up: no jobs' },
  offline: { glyph: '○', word: 'Offline', color: 'idle', border: 'dotted', hint: 'No channel to the center' },
}
const nodeState = (n: TranscoderInfo): NodeState =>
  !n.online ? 'offline' : n.engine && !n.engine.up ? 'engine_down' : n.saturated ? 'saturated' : 'online'

type JobState = TranscodeJobAdmin['state'] | 'no_source' | 'failed'
const JOB_SPEC: Record<JobState, Spec> = {
  running: { glyph: '●', word: 'Running', color: 'live', border: 'solid' },
  starting: { glyph: '◔', word: 'Starting', color: 'start', border: 'dashed' },
  no_source: { glyph: '○', word: 'No source', color: 'warn', border: 'dotted', hint: 'The source stream is not on the transcoder' },
  failed: { glyph: '✕', word: 'Failed', color: 'fail', border: 'solid', hint: 'The engine gave up on this job; the center moves it' },
  ended: { glyph: '○', word: 'Ended', color: 'idle', border: 'dotted' },
}
const jobState = (j: TranscodeJobAdmin): JobState =>
  j.node_status?.state === 'failed' || j.node_status?.state === 'no_source' ? j.node_status.state : j.state

/** 6000 → "6.0 Mbps" */
const mbps = (kbps?: number) => (kbps ? `${(kbps / 1000).toFixed(1)} Mbps` : '—')
const fps = (num: number, den: number) => (den ? Math.round((num / den) * 100) / 100 : 0)
export const targetText = (j: TranscodeJobAdmin) => `${j.target.w}×${j.target.h}@${fps(j.target.fps_num, j.target.fps_den)} ${mbps(j.target.kbps)}`
const speed = (s?: TranscodeNodeJob) => (s && s.speed_milli ? (s.speed_milli / 1000).toFixed(2) : '—')
const lat = (s?: TranscodeNodeJob) => (s && s.lat_p95_us ? `${Math.round(s.lat_p95_us / 1000)} ms` : '—')

export function NetworkTranscodersPage() {
  const nodes = useQuery({ queryKey: TRANSCODERS_KEY, queryFn: () => get<TranscoderInfo[]>('/api/admin/transcoders'), refetchInterval: 3000 })
  const jobs = useQuery({ queryKey: TRANSCODE_JOBS_KEY, queryFn: () => get<TranscodeJobAdmin[]>('/api/admin/transcode/jobs'), refetchInterval: 3000 })
  const [open, setOpen] = useState<string | null>(null)
  const desktop = useMedia('(min-width: 768px)')
  const list = nodes.data ?? []
  const live = list.filter((n) => n.online)
  const cap = live.reduce((a, n) => a + n.max_jobs, 0)
  const busy = live.reduce((a, n) => a + n.jobs, 0)
  const jl = jobs.data ?? []
  // free broadcasts without the mark: counted from the streams' watermark_effective (WM2b)
  const streams = useQuery({ queryKey: ['admin-streams'], queryFn: () => get<AdminStream[]>('/api/admin/streams'), refetchInterval: 3000 })
  const bare = freeWithoutMark(streams.data)
  const toggle = (id: string) => setOpen((o) => (o === id ? null : id))

  return (
    <div className="flex flex-col gap-4" data-testid="network-transcoders">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold">Transcoders</h1>
        <span className="num text-sm text-muted" data-testid="transcoders-capacity">
          {live.length} online · {busy} of {cap} jobs
          {bare > 0 && (
            <span data-testid="transcoders-without-mark" className="text-warn">
              {' · '}<Icon name="state-warn" size={12} className="inline align-[-1px]" /> {bare} free broadcast{bare === 1 ? '' : 's'} without the mark
            </span>
          )}
        </span>
      </div>
      {nodes.error && <ErrorBox text={(nodes.error as Error).message} onRetry={() => void nodes.refetch()} busy={nodes.isFetching} />}

      <NetPanel pad={false} title="Nodes" aside={<span className="text-xs text-muted">GPU nodes that make the per-platform copies</span>}>
        {nodes.isPending ? (
          <div className="p-4"><SkeletonRows rows={2} cols={6} /></div>
        ) : list.length === 0 ? (
          <div className="p-4">
            <Empty title="No transcoder nodes yet" testId="transcoders-empty">
              Add a node with the role Transcoder on a machine with an NVIDIA video card. Until one is online, platforms that need a copy get the original stream and the user sees a warning. Free broadcasts go out without the Strevix mark, and the user is not told.
            </Empty>
          </div>
        ) : desktop ? (
          <table data-testid="transcoders-table" className="w-full table-fixed text-left text-sm">
            <thead className="border-b border-line text-xs text-muted">
              <tr className="[&>th]:px-3 [&>th]:py-2 [&>th]:font-medium [&>th:first-child]:pl-4">
                <th className="w-[22%]">Node</th><th className="w-[13%]">State</th><th className="w-[22%]">Video card</th><th className="w-[17%]">Engine</th><th className="w-[10%]">Jobs</th><th className="w-[10%]">Region</th><th className="w-[6%]"><span className="sr-only">Details</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {list.map((n) => (
                <Fragment key={n.node_id}>
                  <tr data-testid={`transcoder-${n.node_id}`} data-state={nodeState(n)} className="[&>td]:overflow-hidden [&>td]:px-3 [&>td]:py-2.5 [&>td]:align-top [&>td:first-child]:pl-4">
                    <td><p className="truncate font-medium">{n.name || n.node_id}</p><p className="truncate font-mono text-xs text-muted">{n.node_id}</p></td>
                    <td><StateBadge spec={NODE_SPEC[nodeState(n)]} testId={`transcoder-state-${n.node_id}`} /></td>
                    <td><p className="truncate" title={n.engine?.gpu}>{n.engine?.gpu || '—'}</p><p className="truncate font-mono text-xs text-muted">{n.engine?.driver ? `driver ${n.engine.driver}` : ''}</p></td>
                    <td><p className="flex min-w-0 items-center gap-1.5"><span className="truncate font-mono text-xs" title={n.engine?.engine}>{n.engine?.engine || '—'}</span>{canBlend(n.engine?.caps) && <BlendTag id={n.node_id} />}</p><p className="truncate font-mono text-xs text-muted">{n.engine?.sdk ? `SDK ${n.engine.sdk}` : ''}</p></td>
                    <td className="num font-mono text-[13px]" data-testid={`transcoder-jobs-${n.node_id}`}>{n.jobs}<span className="text-muted"> / {n.max_jobs}</span></td>
                    <td className="truncate text-xs text-text-soft">{n.region || '—'}</td>
                    <td className="text-right">
                      <button type="button" data-testid={`transcoder-open-${n.node_id}`} aria-expanded={open === n.node_id} aria-label={`Details of ${n.name || n.node_id}`} onClick={() => toggle(n.node_id)}
                        className={`inline-grid h-8 w-8 place-items-center rounded-md border ${open === n.node_id ? 'border-accent text-accent' : 'border-line-2 text-muted hover:text-text'}`}><GearIcon /></button>
                    </td>
                  </tr>
                  {open === n.node_id && <tr><td colSpan={7} className="bg-panel px-4 py-3"><NodeDetails n={n} /></td></tr>}
                </Fragment>
              ))}
            </tbody>
          </table>
        ) : (
          <ul className="divide-y divide-line" data-testid="transcoders-cards">
            {list.map((n) => (
              <li key={n.node_id} data-testid={`transcoder-${n.node_id}`} data-state={nodeState(n)} className="flex flex-col gap-2 p-4">
                <div className="flex items-center gap-2">
                  <span className="min-w-0 flex-1 truncate font-medium">{n.name || n.node_id}</span>
                  <StateBadge spec={NODE_SPEC[nodeState(n)]} />
                </div>
                <p className="num flex flex-wrap items-center gap-1.5 font-mono text-xs text-text-soft">{n.engine?.gpu || '—'} · {n.jobs} / {n.max_jobs} jobs{canBlend(n.engine?.caps) && <BlendTag id={n.node_id} />}</p>
                <button type="button" onClick={() => toggle(n.node_id)} aria-expanded={open === n.node_id} className="min-h-[44px] self-start text-sm text-accent">{open === n.node_id ? 'Hide details' : 'Details'}</button>
                {open === n.node_id && <NodeDetails n={n} />}
              </li>
            ))}
          </ul>
        )}
      </NetPanel>

      <NetPanel pad={false} title="Jobs" aside={<span className="num text-xs text-muted" data-testid="transcode-jobs-count">{jl.length}</span>} testId="transcode-jobs">
        {jobs.error ? (
          <div className="p-4"><ErrorBox text={(jobs.error as Error).message} onRetry={() => void jobs.refetch()} busy={jobs.isFetching} /></div>
        ) : jl.length === 0 ? (
          <p className="p-4 text-sm text-muted">No transcode jobs: a stream gets one when a destination has “Optimize for the platform” on and the input does not fit the platform.</p>
        ) : (
          <div className="overflow-x-auto">
            <table data-testid="transcode-jobs-table" className="w-full min-w-[880px] text-left text-sm">
              <thead className="border-b border-line text-xs text-muted">
                <tr className="[&>th]:px-3 [&>th]:py-2 [&>th]:font-medium [&>th:first-child]:pl-4">
                  <th>Stream</th><th>Preset</th><th>Makes</th><th>State</th><th>Speed</th><th>Latency p95</th><th>Restarts</th><th>Transcoder</th><th>Route</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {jl.map((j) => (
                  <tr key={j.id} data-testid={`tjob-${j.id}`} data-state={jobState(j)} className="[&>td]:px-3 [&>td]:py-2.5 [&>td]:align-top [&>td:first-child]:pl-4">
                    <td><p className="font-mono text-[13px]">{j.stream}</p><p className="font-mono text-xs text-muted">{j.user_id ? `user ${j.user_id} · ` : ''}{j.id}</p></td>
                    <td className="font-mono text-xs"><span className="inline-flex items-center gap-1.5">{j.preset}{j.kind === 'watermark' && <span className="sx-tag" data-testid={`tjob-wm-${j.id}`} title="The free plan’s Strevix mark">WM</span>}</span></td>
                    <td className="num font-mono text-xs whitespace-nowrap" data-testid={`tjob-target-${j.id}`}>{targetText(j)}</td>
                    <td>
                      <StateBadge spec={JOB_SPEC[jobState(j)]} testId={`tjob-state-${j.id}`} />
                      {j.node_status?.error && <p className="mt-0.5 max-w-48 truncate text-xs text-fail" title={j.node_status.error}>{j.node_status.error}</p>}
                    </td>
                    <td className={`num font-mono text-[13px] ${j.node_status && j.node_status.speed_milli > 0 && j.node_status.speed_milli < 980 ? 'text-warn' : ''}`} data-testid={`tjob-speed-${j.id}`}>{speed(j.node_status)}</td>
                    <td className="num font-mono text-[13px]">{lat(j.node_status)}</td>
                    <td className="num font-mono text-[13px]">{j.node_status?.restarts ?? '—'}</td>
                    <td className="font-mono text-xs">{j.node_id}</td>
                    <td className="font-mono text-xs text-muted"><span title="source → transcoder">{j.src_node_id} → {j.node_id}</span><br /><span title="transcoder → egress">→ {j.egress_node_id}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </NetPanel>
    </div>
  )
}

/** The engine burns in the Strevix mark (caps ∋ blend). */
function BlendTag({ id }: { id: string }) {
  return <span className="sx-tag" data-testid={`transcoder-blend-${id}`} title="Can burn in the Strevix mark">BLEND</span>
}

function NodeDetails({ n }: { n: TranscoderInfo }) {
  const sj = n.stats?.jobs ?? []
  return (
    <div className="flex flex-col gap-3" data-testid={`transcoder-details-${n.node_id}`}>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Fact k="Engine">{n.engine?.engine || '—'}</Fact>
        <Fact k="Video Codec SDK">{n.engine?.sdk || '—'}</Fact>
        <Fact k="Driver">{n.engine?.driver || '—'}</Fact>
        <Fact k="Capacity">{n.max_jobs} jobs</Fact>
      </div>
      {sj.length === 0 ? <p className="text-sm text-muted">No jobs on this node now.</p> : (
        <table className="w-full text-left text-xs">
          <thead className="text-muted"><tr className="[&>th]:py-1 [&>th]:pr-3 [&>th]:font-medium"><th>Job</th><th>State</th><th>Speed</th><th>Frames in / out / dropped</th><th>Latency p95</th><th>Restarts</th></tr></thead>
          <tbody>
            {sj.map((j) => (
              <tr key={j.job_id} className="num font-mono [&>td]:py-1 [&>td]:pr-3">
                <td>{j.job_id}</td><td>{j.state}</td><td>{speed(j)}</td><td>{j.in_frames} / {j.out_frames} / {j.dropped}</td><td>{lat(j)}</td><td>{j.restarts}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="text-xs text-muted">Video card load and memory are reported by the engine to the node; the center does not pass them on yet.</p>
    </div>
  )
}
